/**
 * Data sources and layers added on top of the base map (brief §5.3).
 *
 * Stacking order, bottom to top: region borders → country border → plume
 * polygons → AOI zones → source points → selection. They are (re)created after
 * every style load, since swapping the base map replaces the whole style.
 *
 * Colors come from `readMapColors()` (design-system tokens); paint is refreshed
 * on theme change via `applyColors` rather than rebuilt.
 */
import type { GeoJSONSource, Map as MlMap } from "maplibre-gl";
import type { FeatureCollection } from "geojson";

import type { PlumeGrade } from "../data/types";
import type { FilterValues } from "../store/filtersStore";
import type { LayerFlags } from "../store/layersStore";
import type { Basemap } from "./basemaps";
import { gradeColorExpression, gradeStrokeExpression, type MapColors } from "./colors";

export const SRC = {
  regions: "src-regions",
  country: "src-country",
  plumes: "src-plumes",
  aoi: "src-aoi",
  sources: "src-sources",
} as const;

export const LYR = {
  regionLine: "lyr-region-line",
  regionLabel: "lyr-region-label",
  countryLine: "lyr-country-line",
  plumeFill: "lyr-plume-fill",
  plumeLine: "lyr-plume-line",
  aoiFill: "lyr-aoi-fill",
  aoiLine: "lyr-aoi-line",
  sourceHalo: "lyr-source-halo",
  sourceDot: "lyr-source-dot",
  sourceLabel: "lyr-source-label",
  selectionSource: "lyr-selection-source",
} as const;

/** Layers the pointer can pick — used for click, hover and cursor. */
export const INTERACTIVE_LAYERS = [LYR.plumeFill, LYR.sourceDot, LYR.sourceHalo];

const EMPTY: FeatureCollection = { type: "FeatureCollection", features: [] };
const NONE = "__none__";

function addGeoJSONSource(map: MlMap, id: string) {
  if (!map.getSource(id)) map.addSource(id, { type: "geojson", data: EMPTY });
}

/**
 * Create every data source and layer if absent. Safe to call again after a
 * style reload; existing layers are skipped.
 */
export function ensureLayers(map: MlMap, colors: MapColors): void {
  Object.values(SRC).forEach((id) => addGeoJSONSource(map, id));

  const add = (layer: Record<string, unknown>) => {
    if (!map.getLayer(layer.id as string)) map.addLayer(layer as never);
  };

  // 3 — region borders + labels
  add({
    id: LYR.regionLine,
    type: "line",
    source: SRC.regions,
    paint: { "line-color": colors.borderRegion, "line-width": 0.8 },
  });
  add({
    id: LYR.regionLabel,
    type: "symbol",
    source: SRC.regions,
    minzoom: 5,
    layout: {
      "text-field": ["get", "NAMER"],
      "text-font": ["Noto Sans Regular"],
      "text-size": 11,
      "text-max-width": 8,
    },
    paint: {
      "text-color": colors.borderRegion,
      "text-halo-color": colors.surfaceBase,
      "text-halo-width": 1.2,
    },
  });

  // 4 — country border (thicker, more contrast)
  add({
    id: LYR.countryLine,
    type: "line",
    source: SRC.country,
    paint: { "line-color": colors.borderCountry, "line-width": 1.6 },
  });

  // 5 — plume polygons, colored by grade. Opacity is base × timeFactor, where
  // timeFactor is a per-feature state the timeline drives by detection age
  // (default 1 when the timeline hasn't touched it, so all plumes show).
  add({
    id: LYR.plumeFill,
    type: "fill",
    source: SRC.plumes,
    paint: {
      "fill-color": gradeColorExpression(colors),
      // Hover and selection are both feature state, so they update immediately
      // without replacing the GeoJSON source. Selection wins over hover: the
      // pointer is usually still on the plume that was just clicked, and the
      // two should not add up into an even denser fill.
      "fill-opacity": [
        "*",
        [
          "case",
          ["boolean", ["feature-state", "selected"], false], 0.72,
          ["boolean", ["feature-state", "hover"], false], 0.62,
          0.42,
        ],
        ["coalesce", ["feature-state", "timeFactor"], 1],
      ],
      "fill-opacity-transition": { duration: 140, delay: 0 },
    },
  });
  add({
    id: LYR.plumeLine,
    type: "line",
    source: SRC.plumes,
    // Round joins are not cosmetic here. These polygons are small and wiggly,
    // and MapLibre's default miter join extends every sharp corner to a point,
    // which at map zoom reads as spikes along the outline.
    layout: { "line-join": "round", "line-cap": "round" },
    paint: {
      "line-color": gradeStrokeExpression(colors),
      "line-width": [
        "case",
        ["boolean", ["feature-state", "selected"], false], 1.8,
        ["boolean", ["feature-state", "hover"], false], 1.4,
        0.8,
      ],
      "line-width-transition": { duration: 140, delay: 0 },
      "line-opacity": [
        "*",
        [
          "case",
          ["boolean", ["feature-state", "selected"], false], 1,
          ["boolean", ["feature-state", "hover"], false], 0.85,
          0.55,
        ],
        ["coalesce", ["feature-state", "timeFactor"], 1],
      ],
      "line-opacity-transition": { duration: 140, delay: 0 },
    },
  });

  // 6 — areas of interest (barely-there fill, dashed stroke)
  add({
    id: LYR.aoiFill,
    type: "fill",
    source: SRC.aoi,
    paint: { "fill-color": colors.aoiZoneFill, "fill-opacity": 0.08 },
  });
  add({
    id: LYR.aoiLine,
    type: "line",
    source: SRC.aoi,
    paint: {
      "line-color": colors.aoiZoneStroke,
      "line-width": 1,
      "line-dasharray": [2, 2],
    },
  });

  // 7 — source points (dot + halo) and labels at close zoom
  add({
    id: LYR.sourceHalo,
    type: "circle",
    source: SRC.sources,
    paint: {
      "circle-radius": ["case", ["boolean", ["feature-state", "hover"], false], 12, 9],
      "circle-radius-transition": { duration: 140, delay: 0 },
      // Colored by the source's worst detection grade, like the plumes.
      "circle-color": gradeColorExpression(colors, "maxGrade"),
      "circle-opacity": 0.18,
    },
  });
  add({
    id: LYR.sourceDot,
    type: "circle",
    source: SRC.sources,
    paint: {
      "circle-radius": ["case", ["boolean", ["feature-state", "hover"], false], 6.5, 5],
      "circle-radius-transition": { duration: 140, delay: 0 },
      "circle-color": gradeColorExpression(colors, "maxGrade"),
      "circle-stroke-color": colors.aoiPointHalo,
      "circle-stroke-width": 2,
    },
  });
  add({
    id: LYR.sourceLabel,
    type: "symbol",
    source: SRC.sources,
    minzoom: 7,
    layout: {
      "text-field": ["get", "name"],
      "text-font": ["Noto Sans Regular"],
      "text-size": 12,
      "text-offset": [0, 1.2],
      "text-anchor": "top",
    },
    paint: {
      "text-color": colors.textPrimary,
      "text-halo-color": colors.surfaceBase,
      "text-halo-width": 1.4,
    },
  });

  // 8 — selection ring for source points. Plumes have no layer of their own
  // here: their selection lives in the fill and line layers above, as feature
  // state. A separate ring on top meant a selected plume carried two strokes,
  // its own and the ring's, which is what made the outline look heavy.
  add({
    id: LYR.selectionSource,
    type: "circle",
    source: SRC.sources,
    filter: ["==", ["get", "sourceId"], NONE],
    paint: {
      "circle-radius": 11,
      "circle-color": "rgba(0,0,0,0)",
      "circle-stroke-color": gradeStrokeExpression(colors, "maxGrade"),
      "circle-stroke-width": 1.8,
    },
  });
}

/** Push fresh data into the sources (after load or a style reload). */
export function setLayerData(
  map: MlMap,
  data: {
    plumes?: FeatureCollection;
    sources?: FeatureCollection;
    aoi?: FeatureCollection;
    regions?: FeatureCollection;
    country?: FeatureCollection;
  },
): void {
  const set = (id: string, fc?: FeatureCollection) => {
    const source = map.getSource(id) as GeoJSONSource | undefined;
    if (source && fc) source.setData(fc as never);
  };
  set(SRC.plumes, data.plumes);
  set(SRC.sources, data.sources);
  set(SRC.aoi, data.aoi);
  set(SRC.regions, data.regions);
  set(SRC.country, data.country);
}

/**
 * Swap the base map by replacing only its raster layers, leaving the data
 * layers in place. This avoids `map.setStyle()` — which tears down the whole
 * style and drops the vectors until they're re-added — so points and polygons
 * never disappear on a base-map change.
 */
export function applyBasemap(map: MlMap, basemap: Basemap): void {
  // Remove old base-map layers first, then their now-unreferenced sources.
  for (const layer of map.getStyle().layers ?? []) {
    if (layer.id.startsWith("basemap-")) map.removeLayer(layer.id);
  }
  for (const id of Object.keys(map.getStyle().sources ?? {})) {
    if (id.startsWith("basemap-")) map.removeSource(id);
  }

  // Insert the new base map beneath the lowest data layer so it stays a backdrop.
  const beforeId = map.getLayer(LYR.regionLine) ? LYR.regionLine : undefined;
  basemap.tiles.forEach((template, index) => {
    const sourceId = `basemap-${index}`;
    map.addSource(sourceId, {
      type: "raster",
      tiles: [template],
      tileSize: 256,
      attribution: basemap.attribution,
      maxzoom: basemap.maxZoom,
    });
    map.addLayer(
      {
        id: sourceId,
        type: "raster",
        source: sourceId,
        ...(basemap.rasterPaint ? { paint: basemap.rasterPaint } : {}),
      } as never,
      beforeId,
    );
  });
}

// High-contrast border/label colors for use over photographic imagery.
const IMAGERY = {
  region: "rgba(255,255,255,0.55)",
  country: "rgba(255,255,255,0.92)",
  labelText: "#ffffff",
  labelHalo: "rgba(0,0,0,0.75)",
} as const;

/**
 * Re-apply token colors to every paint property (theme change). When the base
 * map is imagery, borders and labels use a white-on-dark-halo treatment instead
 * of the muted tokens, which are unreadable over a dark photo.
 */
export function applyColors(map: MlMap, colors: MapColors, imagery = false): void {
  const set = (layer: string, prop: string, value: unknown) => {
    if (map.getLayer(layer)) map.setPaintProperty(layer, prop, value as never);
  };
  set(LYR.regionLine, "line-color", imagery ? IMAGERY.region : colors.borderRegion);
  set(LYR.regionLabel, "text-color", imagery ? IMAGERY.labelText : colors.borderRegion);
  set(LYR.regionLabel, "text-halo-color", imagery ? IMAGERY.labelHalo : colors.surfaceBase);
  set(LYR.regionLabel, "text-halo-width", imagery ? 1.6 : 1.2);
  set(LYR.countryLine, "line-color", imagery ? IMAGERY.country : colors.borderCountry);
  set(LYR.plumeFill, "fill-color", gradeColorExpression(colors));
  set(LYR.plumeLine, "line-color", gradeStrokeExpression(colors));
  set(LYR.aoiFill, "fill-color", colors.aoiZoneFill);
  set(LYR.aoiLine, "line-color", colors.aoiZoneStroke);
  set(LYR.sourceHalo, "circle-color", gradeColorExpression(colors, "maxGrade"));
  set(LYR.sourceDot, "circle-color", gradeColorExpression(colors, "maxGrade"));
  set(LYR.sourceDot, "circle-stroke-color", colors.aoiPointHalo);
  set(LYR.sourceLabel, "text-color", imagery ? IMAGERY.labelText : colors.textPrimary);
  set(LYR.sourceLabel, "text-halo-color", imagery ? IMAGERY.labelHalo : colors.surfaceBase);
  set(LYR.sourceLabel, "text-halo-width", imagery ? 1.8 : 1.4);
  set(LYR.selectionSource, "circle-stroke-color", gradeStrokeExpression(colors, "maxGrade"));
}

const VISIBILITY: Record<keyof LayerFlags, string[]> = {
  country: [LYR.countryLine],
  regions: [LYR.regionLine, LYR.regionLabel],
  plumes: [LYR.plumeFill, LYR.plumeLine],
  raster: [], // raster imagery arrives with episode data (stage 6)
  sources: [LYR.sourceHalo, LYR.sourceDot, LYR.sourceLabel],
  aoi: [LYR.aoiFill, LYR.aoiLine],
};

export function applyVisibility(map: MlMap, flags: LayerFlags): void {
  (Object.keys(VISIBILITY) as (keyof LayerFlags)[]).forEach((key) => {
    const visible = flags[key] ? "visible" : "none";
    for (const layer of VISIBILITY[key]) {
      if (map.getLayer(layer)) map.setLayoutProperty(layer, "visibility", visible);
    }
  });
}

/**
 * Combined filter for plumes and sources (brief §6 legend + §7 panel).
 *
 * The legend grades and the panel filters (period, satellites, minimum rate)
 * are merged into one expression per layer, because `setFilter` replaces —
 * they can't be set independently or the last one wins.
 *
 * Plumes are filtered by all of it. Sources are filtered only by grade (the
 * legend), since a source is a persistent facility, not a time-bound detection;
 * hiding facilities by date/satellite would misrepresent them as gone.
 *
 * Only `filter` is touched, i.e. what's rendered. The click handler queries
 * rendered features, so filtered-out plumes simply stop being clickable while
 * visible ones keep working.
 */
export function applyFilters(
  map: MlMap,
  grades: PlumeGrade[],
  filters: FilterValues,
): void {
  const conds: unknown[] = ["all"];
  if (grades.length) conds.push(["in", ["get", "grade"], ["literal", grades]]);
  // ISO YYYY-MM-DD strings compare lexicographically, so string >= works.
  if (filters.from) conds.push([">=", ["get", "date"], filters.from]);
  if (filters.to) conds.push(["<=", ["get", "date"], filters.to]);
  if (filters.satellites.length) {
    conds.push(["in", ["get", "satellite"], ["literal", filters.satellites]]);
  }
  if (filters.minRate > 0) {
    // Unquantified plumes (null rate → -1) fall out once a minimum is set.
    conds.push([">=", ["to-number", ["coalesce", ["get", "emissionRate"], -1]], filters.minRate]);
  }
  const plumeFilter = conds.length > 1 ? conds : null;
  for (const layer of [LYR.plumeFill, LYR.plumeLine]) {
    if (map.getLayer(layer)) map.setFilter(layer, plumeFilter as never);
  }

  // Sources: grade only. A source matches if any active grade is in its array.
  const sourceFilter = grades.length
    ? ["any", ...grades.map((g) => ["in", g, ["get", "grades"]])]
    : null;
  for (const layer of [LYR.sourceHalo, LYR.sourceDot, LYR.sourceLabel]) {
    if (map.getLayer(layer)) map.setFilter(layer, sourceFilter as never);
  }
}

// --- Timeline age → plume opacity (brief §9) ------------------------------

const DAY = 86_400_000;
const FADE_FLOOR = 0.15; // opacity fraction a detection holds at the window edge

interface AgeRecord {
  fid: number;
  dateMs: number;
}

/**
 * timeFactor for one detection: 1 when fresh, fading to FADE_FLOOR at the far
 * edge of the accumulation window, and 0 once it's older than the window or
 * still in the future relative to the cursor.
 */
function timeFactor(cursorMs: number, dateMs: number, windowDays: number): number {
  const ageDays = (cursorMs - dateMs) / DAY;
  if (ageDays < 0 || ageDays > windowDays) return 0;
  const f = 1 - (ageDays / windowDays) * (1 - FADE_FLOOR);
  return Math.round(f * 100) / 100;
}

// Last factor written per feature, so a playback frame only touches what moved.
const lastFactor = new Map<number, number>();

/** Clear the write cache — call after a style reload wipes feature-state. */
export function resetTimeFactors(): void {
  lastFactor.clear();
}

export function applyTimeOpacity(
  map: MlMap,
  records: AgeRecord[],
  cursorMs: number,
  windowDays: number,
): void {
  if (!map.getSource(SRC.plumes)) return;
  for (const r of records) {
    const f = timeFactor(cursorMs, r.dateMs, windowDays);
    if (lastFactor.get(r.fid) === f) continue;
    lastFactor.set(r.fid, f);
    map.setFeatureState({ source: SRC.plumes, id: r.fid }, { timeFactor: f });
  }
}

// Which features currently carry `selected`, so a change only touches the
// features that actually entered or left the selection.
const selectedFids = new Set<number>();

/** Forget the selection cache — call after a style reload wipes feature-state. */
export function resetSelection(): void {
  selectedFids.clear();
}

export function applySelection(
  map: MlMap,
  selection: { episodeId: string | null; sourceId: string | null },
  records: { fid: number; episodeId: string }[],
): void {
  if (map.getSource(SRC.plumes)) {
    const next = new Set<number>();
    if (selection.episodeId !== null) {
      for (const r of records) {
        if (r.episodeId === selection.episodeId) next.add(r.fid);
      }
    }

    for (const fid of selectedFids) {
      if (!next.has(fid)) {
        map.setFeatureState({ source: SRC.plumes, id: fid }, { selected: false });
      }
    }
    for (const fid of next) {
      if (!selectedFids.has(fid)) {
        map.setFeatureState({ source: SRC.plumes, id: fid }, { selected: true });
      }
    }

    selectedFids.clear();
    for (const fid of next) selectedFids.add(fid);
  }

  // Sources live in their own GeoJSON source and have no per-feature state to
  // hang this on, so their ring stays a filtered layer.
  if (map.getLayer(LYR.selectionSource)) {
    map.setFilter(LYR.selectionSource, [
      "==",
      ["get", "sourceId"],
      selection.sourceId ?? NONE,
    ] as never);
  }
}
